import { ReactNode, createContext, useState, useEffect, useContext, useRef } from 'react';
import { VolSurfaceToken } from '../../../model/currency_type.enum';
import { WidgetContext } from '../../../components/v2/widget-renderer/widget-provider';
import { WebSocketContext } from '../../websocket-context/websocket-provider';
import moment from 'moment';
import usePersistentState from '../../use-persistent-state';

export enum WindowEnum {
  MIN10 = '10m',
  H1 = '1H',
  D1 = '1D',
  D2 = '2D',
  D3 = '3D',
  D4 = '4D',
  D5 = '5D',
  D6 = '6D',
  W1 = '1W',
  W2 = '2W',
  M1 = '1M',
  M2 = '2M',
  M3 = '3M',
  M6 = '6M',
  M9 = '9M',
  Y1 = '1Y',
}

export enum MetricEnum {
  ATMF = 'ATMF',
  P5 = '5P',
  P10 = '10P',
  P15 = '15P',
  P20 = '20P',
  P25 = '25P',
  P30 = '30P',
  P35 = '35P',
  P40 = '40P',
  C5 = '5C',
  C10 = '10C',
  C15 = '15C',
  C20 = '20C',
  C25 = '25C',
  C30 = '30C',
  C35 = '35C',
  C40 = '40C',
}

interface VolHeatmapRequestParams {
  tokenKey: VolSurfaceToken;
  window: WindowEnum;
  strike?: string;
  timestamp?: string;
}

export interface VolHeatmapPoint {
  0: number;
  1: number;
  2: number;
  3: number;
}

export interface VolHeatmapDataShape {
  xCategories: string[];
  yCategories: string[];
  points: VolHeatmapPoint[];
  range?: { heatMin: number; heatMax: number };
  precision?: { heat: number; vol: number };
  units?: { heat: string; vol: string };
  asof?: string;
}

interface IVolatilityHeatmapContext {
  data: VolHeatmapDataShape | null;
  isLoading: boolean;
  fetchVolHeatmap: (params: VolHeatmapRequestParams) => void;
  lastTimestamp: string;
  token: VolSurfaceToken;
  setToken: (t: VolSurfaceToken) => void;
  selectedPeriod: WindowEnum;
  setSelectedPeriod: (w: WindowEnum) => void;
  selectedStrikeType: string;
  setSelectedStrikeType: (w: string) => void;
  hasDefaultToken: boolean;
  heatmapTimestamp: React.MutableRefObject<undefined | string>;
  isHeatmapLive: boolean;
  error: boolean;
}

export const VolatilityHeatmapContext = createContext<IVolatilityHeatmapContext>(
  {} as IVolatilityHeatmapContext,
);

interface Props {
  children: ReactNode;
  providerKey: string;
}

export const VolatilityHeatmapProvider = ({ children, providerKey }: Props) => {
  const STATE_PATH = ['VOLATILITY_HEATMAP_PROVIDER', `$${providerKey || 'DEFAULT'}`];

  const { sendMessage, subscribeToChannel, isConnected } = useContext(WebSocketContext);
  const [data, setData] = useState<VolHeatmapDataShape | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastTimestamp, setLastTimestamp] = useState<string>(new Date().toISOString());
  const [isHeatmapLive, setIsHeatmapLive] = useState(true);
  const { token, setToken, hasDefaultToken } = useContext(WidgetContext);

  const [selectedPeriod, setSelectedPeriod] = usePersistentState(
    STATE_PATH,
    'selectedPeriod',
    WindowEnum.M1,
  );
  const [selectedStrikeType, setSelectedStrikeType] = usePersistentState(
    STATE_PATH,
    'selectedStrikeType',
    '',
  );

  const heatmapTimestampRef = useRef('');

  const HEATMAP_ID_KEY = `VOLATILITY_HEATMAP_ID_${providerKey}`;

  const handleResponse = (message: any) => {
    let payload = message;
    if (typeof message === 'string') {
      try {
        payload = JSON.parse(message);
      } catch {
        // noop
      }
    }

    if (payload && payload?.error) {
      setIsLoading(false);
      setError(true);
      return;
    }

    if (!payload || payload.id !== HEATMAP_ID_KEY) return;
    const result = payload.result || {};
    const xCats: string[] = result.xCategories || result.x_categories || [];
    const yCats: string[] = result.yCategories || result.y_categories || [];
    const rawPoints: any[] = Array.isArray(result.data) ? result.data : [];
    const normalizedAsOf: string | undefined =
      result.asof || result.asOf || result.as_at || result.as_at_time || undefined;

    const points: VolHeatmapPoint[] = rawPoints
      .map((p) => {
        const xIdx = xCats.indexOf(p.x);
        const yIdx = yCats.indexOf(p.y);
        if (xIdx === -1 || yIdx === -1) return null;
        const heatVal = typeof p.heat === 'number' ? p.heat : Number(p.heat);
        const volVal = typeof p.vol === 'number' ? p.vol : Number(p.vol);
        if (Number.isNaN(heatVal) || Number.isNaN(volVal)) return null;
        return [xIdx, yIdx, heatVal, volVal] as VolHeatmapPoint;
      })
      .filter(Boolean) as VolHeatmapPoint[];

    const dataShape: VolHeatmapDataShape = {
      xCategories: xCats,
      yCategories: yCats,
      points,
      range: result.range,
      precision: result.precision,
      units: result.units,
      asof: normalizedAsOf,
    };

    setData(dataShape);
    setIsLoading(false);
    const asofIso: string | undefined = normalizedAsOf;
    const nowUtcMs = Date.now();
    let isLive = false;
    const MAX_DIFF_MS = 5 * 60 * 1000; // 5 min

    if (asofIso && typeof asofIso === 'string') {
      const parsed = Date.parse(asofIso);
      if (!Number.isNaN(parsed)) {
        const diff = Math.abs(nowUtcMs - parsed);
        isLive = diff <= MAX_DIFF_MS && parsed <= nowUtcMs + 2000;
        setLastTimestamp(new Date(parsed).toISOString());
      } else {
        setLastTimestamp(new Date().toISOString());
        isLive = false;
      }
    } else {
      setLastTimestamp(new Date().toISOString());
      isLive = true;
    }
    setIsHeatmapLive(isLive);
  };

  const fetchVolHeatmap = ({
    tokenKey,
    window,
    strike = '',
    timestamp,
  }: VolHeatmapRequestParams) => {
    setError(false);
    setIsLoading(true);

    if (timestamp) {
      heatmapTimestampRef.current = timestamp;
    } else {
      heatmapTimestampRef.current = '';
    }

    const req = {
      jsonrpc: '2.0',
      id: HEATMAP_ID_KEY,
      method: 'heatmap_iv',
      params: {
        underlying: tokenKey,
        window,
        format: 'json',
        as_at_time: timestamp ? new Date(timestamp).toISOString() : moment().utc(),
        strike_rrfly: strike,
      },
    };

    sendMessage(JSON.stringify(req));
    subscribeToChannel(HEATMAP_ID_KEY, handleResponse);
  };

  useEffect(() => {
    if (!isConnected) return;

    fetchVolHeatmap({
      tokenKey: token,
      window: selectedPeriod,
      strike: selectedStrikeType,
      timestamp: heatmapTimestampRef.current,
    });
  }, [selectedPeriod, selectedStrikeType, token, isConnected]);

  return (
    <VolatilityHeatmapContext.Provider
      value={{
        data,
        error,
        isLoading,
        fetchVolHeatmap,
        lastTimestamp,
        token,
        setToken,
        selectedPeriod,
        setSelectedPeriod,
        selectedStrikeType,
        setSelectedStrikeType,
        hasDefaultToken,
        heatmapTimestamp: heatmapTimestampRef,
        isHeatmapLive,
      }}
    >
      {children}
    </VolatilityHeatmapContext.Provider>
  );
};
